import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, AlertTriangle, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';

const INR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Dashboard alert: stock expiring within 90 days (or expired). HQ sees per store/warehouse breakdown.
export const ExpiryAlertCard = ({ scope = 'hq', reportPath }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/product-reports/expiry/alerts').then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const none = data.total_items === 0;
  const severe = data.expired_items > 0 || data.within_30_items > 0;

  return (
    <Card className={`mb-6 border ${none ? 'bg-emerald-50 border-emerald-200' : severe ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid="expiry-alert-card">
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className={`p-2 rounded-lg ${none ? 'bg-emerald-600' : severe ? 'bg-red-600' : 'bg-amber-500'} text-white shrink-0`}>
            {none ? <CalendarClock className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            {none ? (
              <p className="font-semibold text-emerald-800">No stock expiring in the next {data.alert_days} days</p>
            ) : (
              <>
                <p className={`font-semibold ${severe ? 'text-red-800' : 'text-amber-800'}`} data-testid="expiry-alert-headline">
                  {data.total_items} stock item(s) expire within {data.alert_days} days
                  {data.expired_items > 0 && <span className="ml-2 text-xs bg-red-600 text-white rounded px-1.5 py-0.5">{data.expired_items} already expired</span>}
                  {data.within_30_items > 0 && <span className="ml-2 text-xs bg-orange-500 text-white rounded px-1.5 py-0.5">{data.within_30_items} within 30 days</span>}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">Stock value at risk {INR(data.stock_value)}</p>
                {scope === 'hq' && data.locations?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.locations.map(l => (
                      <span key={`${l.location_type}-${l.location_id}`} className="text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 text-gray-700" data-testid={`expiry-alert-loc-${l.location_code}`}>
                        <span className="font-semibold">{l.location_code || l.location_name}</span> · {l.items} item(s){l.expired_items ? ` · ${l.expired_items} expired` : ''} · {INR(l.stock_value)}
                        <span className="ml-1 uppercase text-[9px] text-gray-400">{l.location_type}</span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <button onClick={() => navigate(reportPath)} className="text-sm font-medium text-gray-700 hover:text-gray-900 inline-flex items-center gap-1 shrink-0" data-testid="expiry-alert-link">
            Expiry report <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExpiryAlertCard;
